import type {AutoValue, OmitStrict, RichTextContent, Values} from '@wix/devzai-utils-common';
import {
    autoValueCreate,
    autoValueIsAutoValue,
    objectOmit,
    stringTrimPrefix,
    uuidCreate
} from '@wix/devzai-utils-common';
import type {DoppeHideableValue} from './doppe-hideable-value';
import {doppeHideableValueCreateHidden, doppeHideableValueGetVisibleValue} from './doppe-hideable-value';
import type {WixImageResource} from '@wix/devzai-common-wix';
import type {DoppeActionCtaButtonSettings} from './doppe-action-cta-button-settings';
import type {DoppeActionPrice} from './doppe-action-price';
import type {DoppeCheckoutSettingsWithBindings} from './doppe-checkout-action-settings';
import {getDefaultDoppeCheckoutSettingsForAction} from './doppe-checkout-action-settings';
import type {DoppeActionCtaButtonsSettings} from './doppe-action-cta-buttons-settings';

import type {DoppeHtmlSitePagesType} from './doppe-html-site';
import type {DoppeBusinessEntityReference} from './doppe-business-entity';
import {DoppeBusinessEntity} from './doppe-business-entity';
import type {
    BymoPagePropsContentSettingsFilterOptions,
    BymoPagePropsContentSettingsMetadata
} from "../bymo-page-props-metadata/bymo-page-props-metadata";
import {
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    bymoPagePropsFilterContentSettings,
    BymoPagePropsSpecialContentSettingMetadata
} from "../bymo-page-props-metadata/bymo-page-props-metadata";

/**
 * The types of CTA buttons that can be used in any action that supports CTA buttons.
 */
export const DoppeActionCommonCTATypes = {
    Link: 'Link',
    Whatsapp: 'Whatsapp',
    Call: 'Call',
    Navigate: 'Navigate',
    Email: 'Email',
    FacebookMessage: 'FacebookMessage',
    InstagramMessage: 'InstagramMessage',
    Share: 'Share',
    Checkout: 'Checkout',
    /**
     * @deprecated Use {@link DoppeActionCTAType.Link} with {@link DoppeLinkSpec.Page} instead.
     * Existing OpenPage buttons are migrated to Link with DoppeLinkSpec.Page via OpenPageCtaToLinkSpec migration.
     */
    OpenPage: 'OpenPage',
    IsracardPay: 'IsracardPay',
    WixEcomCheckout: 'WixEcomCheckout'
} as const;

export type DoppeActionCommonCTATypes = Values<typeof DoppeActionCommonCTATypes>;

/**
 * The types of CTA buttons that can be used in specific actions that support CTA buttons.
 * 
 * @deprecated OpenBlogPost, OpenProduct, OpenService, OpenEvent are deprecated.
 * They are automatically migrated to Link type with "Same as source URL" in 
 * doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration.
 */
export const DoppeActionTypeSpecificCTATypes = {
    /** @deprecated Use Link type with "Same as source URL" instead */
    OpenBlogPost: 'OpenBlogPost',
    /** @deprecated Use Link type with "Same as source URL" instead */
    OpenProduct: 'OpenProduct',
    /** @deprecated Use Link type with "Same as source URL" instead */
    OpenService: 'OpenService',
    /** @deprecated Use Link type with "Same as source URL" instead */
    OpenEvent: 'OpenEvent',
    /** @deprecated Use Link type with "Same as source URL" instead */
    OpenPricingPlan: 'OpenPricingPlan',
    SaveContact: 'SaveContact',
} as const;

export type DoppeActionTypeSpecificCTATypes = Values<typeof DoppeActionTypeSpecificCTATypes>;

export const DoppeActionCTAType = {
    ...DoppeActionCommonCTATypes,
    ...DoppeActionTypeSpecificCTATypes
} as const;

export type DoppeActionCTAType = Values<typeof DoppeActionCTAType>;

export const DoppeActionCTATypeLinkAutoSource = {
    SameAsSourceURL: 'SameAsSourceURL',
} as const;

export type DoppeActionCTATypeLinkAutoSource = Values<typeof DoppeActionCTATypeLinkAutoSource>;

export const DoppeActionCTATypeLinkOpenType = {
    NewTab: 'NewTab',
    SameTab: 'SameTab',
    InPage: 'InPage',
} as const;

export type DoppeActionCTATypeLinkOpenType = Values<typeof DoppeActionCTATypeLinkOpenType>;

/**
 * Link spec type discriminator - determines what type of link spec object it is
 */
export const DoppeLinkSpecType = {
    /**
     * Internal page (inner page or vertical page)
     */
    Page: 'Page',
    /**
     * Vertical entity item (Product, Blog Post, Event, Service)
     */
    Entity: 'Entity',

    URL: 'Url',
} as const;

export type DoppeLinkSpecType = Values<typeof DoppeLinkSpecType>;

/**
 * Entity types for vertical item links
 */
export const DoppeLinkSpecEntityType = {
    Product: 'Product',
    BlogPost: 'BlogPost',
    Event: 'Event',
    Service: 'Service',
    PricingPlan: 'PricingPlan'
} as const;

export type DoppeLinkSpecEntityType = Values<typeof DoppeLinkSpecEntityType>;

/**
 * Link specification - can be a URL string or an object describing a page/entity
 * 
 * @example
 * // External URL
 * const urlSpec: DoppeLinkSpec = 'https://example.com';
 * 
 * // Page link
 * const pageSpec: DoppeLinkSpec = { type: 'Page', pageId: 'page-123' };
 * 
 * // Entity link
 * const entitySpec: DoppeLinkSpec = { type: 'Entity', entityType: 'Product', entityId: 'prod-456' };
 */
export type DoppeLinkSpec = string | DoppeLinkSpec.Page | DoppeLinkSpec.Entity;

export namespace DoppeLinkSpec {
    /**
     * Page link specification - links to an internal page
     */
    export interface Page {
        type: typeof DoppeLinkSpecType.Page;
        /**
         * The page ID to navigate to (inner page or vertical page ID)
         */
        pageId: string;
    }
    
    /**
     * Entity link specification - links to a vertical entity item
     */
    export interface Entity {
        type: typeof DoppeLinkSpecType.Entity;
        /**
         * The type of entity (Product, BlogPost, Event, Service)
         */
        entityType: DoppeLinkSpecEntityType;
        /**
         * The entity item ID
         */
        entityId: string;
    }
}

/**
 * Type guard to check if a link spec is a string URL
 */
export function doppeLinkSpecIsUrl(linkSpec: DoppeLinkSpec): linkSpec is string {
    return typeof linkSpec === 'string';
}

/**
 * Type guard to check if a link spec is a page link
 */
export function doppeLinkSpecIsPage(linkSpec: DoppeLinkSpec): linkSpec is DoppeLinkSpec.Page {
    return typeof linkSpec === 'object' && linkSpec !== null && linkSpec.type === DoppeLinkSpecType.Page;
}

/**
 * Type guard to check if a link spec is an entity link
 */
export function doppeLinkSpecIsEntity(linkSpec: DoppeLinkSpec): linkSpec is DoppeLinkSpec.Entity {
    return typeof linkSpec === 'object' && linkSpec !== null && linkSpec.type === DoppeLinkSpecType.Entity;
}

/**
 * Gets the link type from a link spec ('Url', 'Page', or 'Entity')
 */
export function doppeLinkSpecGetType(linkSpec: DoppeLinkSpec): DoppeLinkSpecType {
    if (doppeLinkSpecIsUrl(linkSpec)) {
        return DoppeLinkSpecType.URL;
    }
    return linkSpec.type;
}

/**
 * Creates a default empty link spec (empty URL string)
 */
export function doppeLinkSpecCreateDefault(): DoppeLinkSpec {
    return '#';
}

/**
 * Creates a page link spec
 */
export function doppeLinkSpecCreatePage(pageId: string): DoppeLinkSpec.Page {
    return {
        type: DoppeLinkSpecType.Page,
        pageId: pageId
    };
}

/**
 * Creates an entity link spec
 */
export function doppeLinkSpecCreateEntity(entityType: DoppeLinkSpecEntityType, entityId: string): DoppeLinkSpec.Entity {
    return {
        type: DoppeLinkSpecType.Entity,
        entityType: entityType,
        entityId: entityId
    };
}

/**
 * Extracts the destination type from a DoppeLinkSpec for UI purposes
 */
export function doppeLinkSpecGetDestinationType(linkSource: DoppeActionCTATypeLinkSource): DoppeLinkSpecType {
    if (autoValueIsAutoValue(linkSource)) {
        return DoppeLinkSpecType.URL;
    }
    return doppeLinkSpecGetType(linkSource);
}

/**
 * Checks if a link source is a DoppeLinkSpec object (Page or Entity, not a string URL or AutoValue)
 */
export function doppeLinkSpecIsLinkSpecObject(linkSource: DoppeActionCTATypeLinkSource): linkSource is DoppeLinkSpec.Page | DoppeLinkSpec.Entity {
    if (autoValueIsAutoValue(linkSource)) {
        return false;
    }
    return typeof linkSource === 'object' && linkSource !== null && 'type' in linkSource;
}

/**
 * Extracts page ID from a DoppeLinkSpec if it's a page link
 */
export function doppeLinkSpecGetPageId(linkSource: DoppeActionCTATypeLinkSource): string | null {
    if (autoValueIsAutoValue(linkSource)) {
        return null;
    }
    if (doppeLinkSpecIsPage(linkSource)) {
        return linkSource.pageId;
    }
    return null;
}

/**
 * Extracts entity info from a DoppeLinkSpec if it's an entity link
 */
export function doppeLinkSpecGetEntityInfo(linkSource: DoppeActionCTATypeLinkSource): { entityType: DoppeLinkSpecEntityType; entityId: string } | null {
    if (autoValueIsAutoValue(linkSource)) {
        return null;
    }
    if (doppeLinkSpecIsEntity(linkSource)) {
        return {
            entityType: linkSource.entityType,
            entityId: linkSource.entityId
        };
    }
    return null;
}

/**
 * Extracts URL string from a DoppeLinkSpec if it's a URL
 */
export function doppeLinkSpecGetUrlString(linkSource: DoppeActionCTATypeLinkSource): string | null {
    if (autoValueIsAutoValue(linkSource)) {
        return null; // AutoValue means "same as source URL"
    }
    if (doppeLinkSpecIsUrl(linkSource)) {
        return linkSource;
    }
    return null;
}

/**
 * Checks if a DoppeLinkSpec is empty (no valid destination)
 * - For URL strings: empty string is empty
 * - For Page links: missing pageId is empty
 * - For Entity links: missing entityId is empty
 */
export function doppeLinkSpecIsEmpty(linkSpec: DoppeLinkSpec): boolean {
    if (doppeLinkSpecIsUrl(linkSpec)) {
        return !linkSpec || linkSpec.trim() === '';
    }
    if (doppeLinkSpecIsPage(linkSpec)) {
        return !linkSpec.pageId;
    }
    if (doppeLinkSpecIsEntity(linkSpec)) {
        return !linkSpec.entityId;
    }
    return true;
}

/**
 * Link source for CTA buttons - can be an AutoValue or a DoppeLinkSpec
 */
export type DoppeActionCTATypeLinkSource = AutoValue<DoppeActionCTATypeLinkAutoSource> | DoppeLinkSpec;

/**
 * Resolves a DoppeLinkSpec to a DoppeBusinessEntityReference if it's an entity link.
 * Returns null for URL or Page links.
 */
export function doppeLinkSpecResolveBusinessEntityReference(linkSpec: DoppeLinkSpec): DoppeBusinessEntityReference | null {
    if (!doppeLinkSpecIsEntity(linkSpec)) {
        return null;
    }
    
    const entityType = linkSpec.entityType;
    const entityId = linkSpec.entityId;
    
    if (!entityId) {
        return null;
    }
    
    switch (entityType) {
        case DoppeLinkSpecEntityType.Product:
            return {
                businessEntity: DoppeBusinessEntity.HoppProduct,
                id: entityId
            };
        case DoppeLinkSpecEntityType.Service:
            return {
                businessEntity: DoppeBusinessEntity.HoppService,
                id: entityId
            };
        case DoppeLinkSpecEntityType.BlogPost:
            return {
                businessEntity: DoppeBusinessEntity.HoppBlogPost,
                id: entityId
            };
        case DoppeLinkSpecEntityType.Event:
            return {
                businessEntity: DoppeBusinessEntity.HoppEvent,
                id: entityId
            };
        default:
            return null;
    }
}


export type DoppeActionCTAButtonSingle = DoppeActionCTAButton.SingleButton;
export type DoppeActionCTAButtonMultiple = DoppeActionCTAButton.MultipleButton;

export type DoppeActionCTAButton = DoppeActionCTAButton.BaseType;

export namespace DoppeActionCTAButton {

    export type BaseType = {
        settings: DoppeActionSettingsCTAButtonSettings;
    }

    export type SingleButton = DoppeActionCTAButton.BaseType & {
        text: DoppeHideableValue<string>;
        showButton: boolean;
    }

    export type MultipleButtonBase = DoppeActionCTAButton.BaseType & {
        isVisible: boolean;
    }

    export type MultipleButton = DoppeActionCTAButton.MultipleButtonBase & {
        text: string;
        isRequired: boolean;
        id: string;
    }

    export type MultipleButtonOverride = DoppeActionCTAButton.MultipleButtonBase & {
        useDefaultContent: boolean;
    }

    export type MultipleButtonWithOverride =
        DoppeActionCTAButton.MultipleButton
        & DoppeActionCTAButton.MultipleButtonOverride
        & {
        isDefaultButton: boolean;
    }
}

export function doppeActionCtaButtonCreateFromListAndOverride(rootId: string, actionCtaButton: DoppeActionCTAButton.MultipleButton, override: DoppeActionCTAButton.MultipleButtonOverride) {
    return {
        ...actionCtaButton,
        ...override,
        isRequired: true,
        id: doppeActionCtaButtonCreateFromListAndOverrideId(rootId, actionCtaButton.id)
    } as DoppeActionCTAButton.MultipleButton;
}

export function doppeActionCtaButtonCreateFromListAndOverrideId(rootId: string, actionCtaButtonId: string) {
    return rootId + actionCtaButtonId;
}

export function doppeActionCtaButtonContactListOverrideId(rootId: string, actionCtaButtonId: string) {
    return stringTrimPrefix(actionCtaButtonId, rootId);
}

export function doppeActionCtaButtonConvertSingleToMultipleButtons(ctaButton: DoppeActionCTAButton.SingleButton) {
    return [{
        text: doppeHideableValueGetVisibleValue(ctaButton.text, ''),
        isVisible: ctaButton.showButton,
        isRequired: true,
        id: uuidCreate(),
        settings: ctaButton.settings
    }] satisfies DoppeActionCTAButton.MultipleButton[];
}

export type DoppeActionSettingsCTAButtonSettings =
    | DoppeActionSettingsCTAButtonSettings.Link
    | DoppeActionSettingsCTAButtonSettings.Whatsapp
    | DoppeActionSettingsCTAButtonSettings.Call
    | DoppeActionSettingsCTAButtonSettings.Navigate
    | DoppeActionSettingsCTAButtonSettings.Email
    | DoppeActionSettingsCTAButtonSettings.InstagramMessage
    | DoppeActionSettingsCTAButtonSettings.FacebookMessage
    | DoppeActionSettingsCTAButtonSettings.SaveContact
    | DoppeActionSettingsCTAButtonSettings.Share
    | DoppeActionSettingsCTAButtonSettings.Checkout
    | DoppeActionSettingsCTAButtonSettings.OpenProduct
    | DoppeActionSettingsCTAButtonSettings.OpenBlogPost
    | DoppeActionSettingsCTAButtonSettings.OpenService
    | DoppeActionSettingsCTAButtonSettings.OpenEvent
    | DoppeActionSettingsCTAButtonSettings.OpenPricingPlan
    | DoppeActionSettingsCTAButtonSettings.OpenPage
    | DoppeActionSettingsCTAButtonSettings.IsracardPay
    | DoppeActionSettingsCTAButtonSettings.WixEcomCheckout;

export namespace DoppeActionSettingsCTAButtonSettings {

    export type Override = Pick<DoppeActionCTAButtonSingle, 'showButton' | 'settings'>

    type BaseType<T extends DoppeActionCTAType> = {
        type: T;
    }

    export type Link = BaseType<typeof DoppeActionCTAType.Link> & {
        /**
         * Link specification - can be:
         * - AutoValue for "Same as source URL"
         * - A string URL (e.g., 'https://example.com')
         * - A DoppeLinkSpec.Page object (e.g., { type: 'Page', pageId: '...' })
         * - A DoppeLinkSpec.Entity object (e.g., { type: 'Entity', entityType: 'Product', entityId: '...' })
         */
        url: DoppeActionCTATypeLinkSource;
        openType: DoppeActionCTATypeLinkOpenType;
    };

    export type IsracardPay = BaseType<typeof DoppeActionCTAType.IsracardPay> & {
        url: string;
        openType: DoppeActionCTATypeLinkOpenType;
    };

    /** @deprecated Use Link type with "Same as source URL" instead */
    export type OpenProduct = BaseType<typeof DoppeActionCTAType.OpenProduct> & {
        openType: DoppeActionCTATypeLinkOpenType;
    };

    /** @deprecated Use Link type with "Same as source URL" instead */
    export type OpenBlogPost = BaseType<typeof DoppeActionCTAType.OpenBlogPost> & {
        openType: DoppeActionCTATypeLinkOpenType;
    };

    /** @deprecated Use Link type with "Same as source URL" instead */
    export type OpenService = BaseType<typeof DoppeActionCTAType.OpenService> & {
        openType: DoppeActionCTATypeLinkOpenType;
    };

    /** @deprecated Use Link type with "Same as source URL" instead */
    export type OpenEvent = BaseType<typeof DoppeActionCTAType.OpenEvent> & {
        openType: DoppeActionCTATypeLinkOpenType;
    };

    /** @deprecated Use Link type with "Same as source URL" instead */
    export type OpenPricingPlan = BaseType<typeof DoppeActionCTAType.OpenPricingPlan> & {
        openType: DoppeActionCTATypeLinkOpenType;
    };

    export type OpenPage = BaseType<typeof DoppeActionCTAType.OpenPage> & {
        openType: DoppeActionCTATypeLinkOpenType;
        pageType: DoppeHtmlSitePagesType | null;
    };


    export type Whatsapp = BaseType<typeof DoppeActionCTAType.Whatsapp> & {
        phone: string;
        customMessage: DoppeHideableValue<string>;
    };

    export type Call = BaseType<typeof DoppeActionCTAType.Call> & {
        phone: string;
    };

    export type Email = BaseType<typeof DoppeActionCTAType.Email> & {
        email: string;
        customSubject: DoppeHideableValue<string>;
        customMessage: DoppeHideableValue<string>;
    };

    export type Navigate = BaseType<typeof DoppeActionCTAType.Navigate> & {
        address: string;
    };

    export type FacebookMessage = BaseType<typeof DoppeActionCTAType.FacebookMessage> & {
        userName: string;
        customMessage: DoppeHideableValue<string>
    };

    export type InstagramMessage = BaseType<typeof DoppeActionCTAType.InstagramMessage> & {
        userName: string;
    };

    export type SaveContact = BaseType<typeof DoppeActionCTAType.SaveContact> & {
        notes: string;
        saveContactProfileImage: DoppeHideableValue<WixImageResource | null>
    }

    export type Share = BaseType<typeof DoppeActionCTAType.Share> & {
        url: DoppeActionCTATypeLinkSource;
        title: string;
        description: string;
    }

    export type Checkout = BaseType<typeof DoppeActionCTAType.Checkout> & {
        price: DoppeActionPrice;
        name: DoppeHideableValue<string>;
        description: DoppeHideableValue<RichTextContent>;
        checkoutSettings: DoppeCheckoutSettingsWithBindings;
    }

    /**
     * WixEcomCheckout CTA button - supports catalog items or custom products
     */
    export type WixEcomCheckout = WixEcomCheckout.CatalogItem | WixEcomCheckout.CustomProduct;

    export namespace WixEcomCheckout {
        interface Base<PRODUCT_TYPE extends WixEcomCheckoutProductType> {
            type: typeof DoppeActionCTAType.WixEcomCheckout;
            productType: PRODUCT_TYPE;
        }

        export interface CatalogItem extends Base<typeof WixEcomCheckoutProductType.CatalogItem> {
            productId: string;
        }

        export interface CustomProduct extends Base<typeof WixEcomCheckoutProductType.CustomProduct> {
            productName: string;
            price: DoppeActionPrice;
            quantity: number;
            itemKind: WixEcomCheckoutItemKind;
            customItemTypeDescription: DoppeHideableValue<string>;
        }
    }

}

/**
 * Product type for WixEcomCheckout - determines if using a catalog product or custom
 */
export const WixEcomCheckoutProductType = {
    CatalogItem: 'CatalogItem',
    CustomProduct: 'CustomProduct',
} as const;

export type WixEcomCheckoutProductType = Values<typeof WixEcomCheckoutProductType>;

/**
 * Item kind for custom products in WixEcomCheckout
 */
export const WixEcomCheckoutItemKind = {
    Physical: 'physical',
    Digital: 'digital',
    GiftCard: 'gift_card',
    Service: 'service',
    Custom: 'custom',
} as const;

export type WixEcomCheckoutItemKind = Values<typeof WixEcomCheckoutItemKind>;

export const DoppeActionCTAButtonsSettingsMetadata = {
    [DoppeActionCTAType.Link]: {
        url: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.LinkSource, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
    [DoppeActionCTAType.Whatsapp]: {
        phone: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Phone, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
        customMessage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
    [DoppeActionCTAType.Call]: {
        phone: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Phone, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
    [DoppeActionCTAType.Navigate]: {
        address: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
    [DoppeActionCTAType.Email]: {
        email: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
        customSubject: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
        customMessage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
    [DoppeActionCTAType.InstagramMessage]: {
        userName: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
    [DoppeActionCTAType.FacebookMessage]: {
        userName: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
        customMessage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
    [DoppeActionCTAType.SaveContact]: {
        notes: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
        saveContactProfileImage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.SingleImage, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
    [DoppeActionCTAType.Share]: {
        url: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.LinkSource, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
        title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
        description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
    [DoppeActionCTAType.Checkout]: {
        price: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
        name: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
        description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
        checkoutSettings: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
    },
    [DoppeActionCTAType.OpenBlogPost]: {},
    [DoppeActionCTAType.OpenProduct]: {},
    [DoppeActionCTAType.OpenService]: {},
    [DoppeActionCTAType.OpenEvent]: {},
    [DoppeActionCTAType.OpenPricingPlan]: {},
    [DoppeActionCTAType.OpenPage]: {},
    [DoppeActionCTAType.IsracardPay]: {
        url: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Url, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
    [DoppeActionCTAType.WixEcomCheckout]: {
        productType: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
    },
} satisfies {
    [BUTTON in DoppeActionSettingsCTAButtonSettings as BUTTON['type']]: BymoPagePropsContentSettingsMetadata<OmitStrict<BUTTON, 'type'>>;
}

export const DoppeActionCTAButtonGeneralSettingsMetadata = {
    text: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    id: BymoPagePropsSpecialContentSettingMetadata.Ignored,
} satisfies BymoPagePropsContentSettingsMetadata<DoppeActionCTAButton.MultipleButton> | BymoPagePropsSpecialContentSettingMetadata;

export function doppeActionCTAButtonFilterContentSettings(
    actionCTAButton: DoppeActionCTAButton.MultipleButton,
    options: BymoPagePropsContentSettingsFilterOptions
) {
    return {
        ...bymoPagePropsFilterContentSettings(objectOmit(actionCTAButton, ['id']), DoppeActionCTAButtonGeneralSettingsMetadata, options),
        settings: bymoPagePropsFilterContentSettings(actionCTAButton.settings, DoppeActionCTAButtonsSettingsMetadata[actionCTAButton.settings.type], options)
    }
}

export function doppeActionMultipleCTAButtonIsVisible(ctaButton: DoppeActionCTAButton.MultipleButton) {
    return ctaButton.isVisible;
}

export function doppeActionCTAButtonIsVisible(ctaButton: DoppeActionCTAButton.SingleButton) {
    return ctaButton.showButton;
}

export function doppeActionCTAButtonCreateLinkSameAsSource(options: {
    text: string,
    hidden?: boolean
}) {

    const {
        text,
        hidden = false
    } = options;

    return {
        settings: {
            type: DoppeActionCTAType.Link,
            url: autoValueCreate(DoppeActionCTATypeLinkAutoSource.SameAsSourceURL),
            openType: DoppeActionCTATypeLinkOpenType.SameTab,
        },
        text: text,
        showButton: !hidden
    } satisfies DoppeActionCTAButton.SingleButton;
}

export function doppeActionMultipleCTAButtonCreateLinkSameAsSource(options: {
    text: string,
    hidden?: boolean
    isRequired?: boolean
}) {

    const {
        text,
        hidden = false,
        isRequired = false
    } = options;

    return {
        settings: {
            type: DoppeActionCTAType.Link,
            url: autoValueCreate(DoppeActionCTATypeLinkAutoSource.SameAsSourceURL),
            openType: DoppeActionCTATypeLinkOpenType.SameTab,
        },
        text: text,
        isVisible: !hidden,
        isRequired: isRequired,
        id: uuidCreate(),
    } satisfies DoppeActionCTAButton.MultipleButton
}

export function doppeActionMultipleCTAButtonCreate(options: {
    text: string,
    hidden?: boolean
    isRequired?: boolean,
    settings: DoppeActionSettingsCTAButtonSettings;
}) {

    const {
        text,
        hidden = false,
        isRequired = false,
        settings
    } = options;

    return {
        settings: settings,
        text: text,
        isVisible: !hidden,
        isRequired: isRequired,
        id: uuidCreate(),
    } satisfies DoppeActionCTAButton.MultipleButton
}

export function doppeActionCtaButtonResetSettingsForLockNew<T extends DoppeActionCTAButton.BaseType>(ctaButton: T) {
    const isSameSourceURl = (
            ctaButton.settings.type === DoppeActionCTAType.Link ||
            ctaButton.settings.type === DoppeActionCTAType.Share
        )
        && autoValueIsAutoValue(ctaButton.settings.url);
    ctaButton.settings = doppeActionCtaButtonTypeGetDefaultSettings(ctaButton.settings.type, isSameSourceURl);
}

export function doppeActionCtaButtonResetSettingsForLock(actionSettings: DoppeActionCtaButtonSettings) {
    const isSameSourceURl = (
            actionSettings.ctaButton.settings.type === DoppeActionCTAType.Link ||
            actionSettings.ctaButton.settings.type === DoppeActionCTAType.Share
        )
        && autoValueIsAutoValue(actionSettings.ctaButton.settings.url
        );
    actionSettings.ctaButton.settings = doppeActionCtaButtonTypeGetDefaultSettings(actionSettings.ctaButton.settings.type, isSameSourceURl);
}

export function doppeActionCtaButtonsResetSettingsForLock(actionSettings: DoppeActionCtaButtonsSettings) {
    actionSettings.ctaButtons.forEach(ctaButton => {
        doppeActionCtaButtonResetSettingsForLockNew(ctaButton);
    });
}

export function doppeActionCtaButtonTypeGetDefaultSettings(
    type: DoppeActionCTAType, 
    hasSourceURL: boolean, 
    hoppPages?: DoppeHtmlSitePagesType[]
) {
    
    switch (type) {
        case DoppeActionCTAType.Checkout: {
            return {
                type: DoppeActionCTAType.Checkout,
                price: {
                    actualPrice: 20,
                    currencyCode: 'USD'
                },
                name: doppeHideableValueCreateHidden(''),
                description: doppeHideableValueCreateHidden(''),
                checkoutSettings: getDefaultDoppeCheckoutSettingsForAction()
            } satisfies DoppeActionSettingsCTAButtonSettings.Checkout;
        }
        case DoppeActionCTAType.Whatsapp: {
            return {
                type: DoppeActionCTAType.Whatsapp,
                phone: '',
                customMessage: '',
            };
        }
        case DoppeActionCTAType.Call: {
            return {
                type: DoppeActionCTAType.Call,
                phone: '',
            };
        }
        case DoppeActionCTAType.Share:
            return {
                type: DoppeActionCTAType.Share,
                url: hasSourceURL ? autoValueCreate(DoppeActionCTATypeLinkAutoSource.SameAsSourceURL) : '',
                title: '',
                description: '',
            };
        case DoppeActionCTAType.Link:
            return {
                type: DoppeActionCTAType.Link,
                url: hasSourceURL ? autoValueCreate(DoppeActionCTATypeLinkAutoSource.SameAsSourceURL) : '',
                openType: DoppeActionCTATypeLinkOpenType.SameTab,
            };
        case DoppeActionCTAType.IsracardPay:
            return {
                type: DoppeActionCTAType.IsracardPay,
                url: '',
                openType: DoppeActionCTATypeLinkOpenType.InPage,
            };
        case DoppeActionCTAType.Email: {
            return {
                type: DoppeActionCTAType.Email,
                email: '',
                customMessage: '',
                customSubject: ''
            };
        }
        case DoppeActionCTAType.Navigate: {
            return {
                type: DoppeActionCTAType.Navigate,
                address: '',
            };
        }
        case DoppeActionCTAType.FacebookMessage: {
            return {
                type: DoppeActionCTAType.FacebookMessage,
                userName: '',
                customMessage: '',
            };
        }
        case DoppeActionCTAType.InstagramMessage: {
            return {
                type: DoppeActionCTAType.InstagramMessage,
                userName: '',
            };
        }
        case DoppeActionCTAType.SaveContact: {
            return {
                type: DoppeActionCTAType.SaveContact,
                notes: '',
                saveContactProfileImage: doppeHideableValueCreateHidden(null),
            };
        }
        case DoppeActionCTAType.OpenProduct: {
            return {
                type: DoppeActionCTAType.OpenProduct,
                openType: DoppeActionCTATypeLinkOpenType.SameTab,
            };
        }
        case DoppeActionCTAType.OpenPage: {
            return {
                type: DoppeActionCTAType.OpenPage,
                openType: DoppeActionCTATypeLinkOpenType.SameTab,
                pageType: hoppPages?.[0] ?? null,
            };
        }
        case DoppeActionCTAType.OpenBlogPost: {
            return {
                type: DoppeActionCTAType.OpenBlogPost,
                openType: DoppeActionCTATypeLinkOpenType.SameTab,
            };
        }
        case DoppeActionCTAType.OpenService: {
            return {
                type: DoppeActionCTAType.OpenService,
                openType: DoppeActionCTATypeLinkOpenType.SameTab,
            };
        }
        case DoppeActionCTAType.OpenEvent: {
            return {
                type: DoppeActionCTAType.OpenEvent,
                openType: DoppeActionCTATypeLinkOpenType.SameTab,
            };
        }
        case DoppeActionCTAType.OpenPricingPlan: {
            return {
                type: DoppeActionCTAType.OpenPricingPlan,
                openType: DoppeActionCTATypeLinkOpenType.SameTab,
            };
        }
        case DoppeActionCTAType.WixEcomCheckout: {
            return {
                type: DoppeActionCTAType.WixEcomCheckout,
                productType: WixEcomCheckoutProductType.CustomProduct,
                productName: '',
                price: {
                    actualPrice: 20,
                    currencyCode: 'USD'
                },
                quantity: 1,
                itemKind: WixEcomCheckoutItemKind.Custom,
                customItemTypeDescription: doppeHideableValueCreateHidden(''),
            } satisfies DoppeActionSettingsCTAButtonSettings.WixEcomCheckout;
        }
    }
}

export function doppeActionCtaButtonTypeOpenUrl(ctaSettings: DoppeActionSettingsCTAButtonSettings) {
    switch (ctaSettings.type) {
        case DoppeActionCTAType.Call:
        case DoppeActionCTAType.Email:
        case DoppeActionCTAType.Whatsapp:
        case DoppeActionCTAType.Navigate:
        case DoppeActionCTAType.FacebookMessage: {
            return true;
        }
        case DoppeActionCTAType.InstagramMessage: {
            return true;
        }
        case DoppeActionCTAType.OpenBlogPost:
        case DoppeActionCTAType.OpenPage:
        case DoppeActionCTAType.OpenProduct:
        case DoppeActionCTAType.OpenService:
        case DoppeActionCTAType.OpenEvent:
        case DoppeActionCTAType.OpenPricingPlan:
        case DoppeActionCTAType.IsracardPay: {
            return ctaSettings.openType !== DoppeActionCTATypeLinkOpenType.InPage;
        }
        case DoppeActionCTAType.Link: {
            // For Link type, check if the url is a LinkSpec object (page/entity)
            const url = ctaSettings.url;
            if (!autoValueIsAutoValue(url) && !doppeLinkSpecIsUrl(url)) {
                // Page and Entity destinations are handled by internal router
                return false;
            }
            return ctaSettings.openType !== DoppeActionCTATypeLinkOpenType.InPage;
        }
        case DoppeActionCTAType.Checkout:
        case DoppeActionCTAType.Share:
        case DoppeActionCTAType.SaveContact:
        case DoppeActionCTAType.WixEcomCheckout: {
            return false;
        }
    }
}

export function doppeActionCtaButtonLinkResolveTarget(ctaSettings: DoppeActionSettingsCTAButtonSettings) {

    switch (ctaSettings.type) {
        case DoppeActionCTAType.OpenBlogPost:
        case DoppeActionCTAType.OpenPage:
        case DoppeActionCTAType.OpenProduct:
        case DoppeActionCTAType.OpenService:
        case DoppeActionCTAType.OpenEvent:
        case DoppeActionCTAType.Link: {

            if (ctaSettings.openType === DoppeActionCTATypeLinkOpenType.NewTab) {
                return '_blank';
            } else if (ctaSettings.openType === DoppeActionCTATypeLinkOpenType.SameTab) {
                return '_self';
            } else {
                return undefined;
            }
        }
        default: {
            return undefined;
        }
    }
}

